import React, {MouseEvent, useEffect, useMemo, useState, useCallback} from 'react';
import styles from './Presentations.module.scss';
import { Lesson as LessonType, Content } from '../../../../types';
import RightIcon from '../../../../assets/icons/chewron_right.svg?react';
import Lesson from '../lesson/Lesson';
import AddLessonButton from '../add-lesson-button/AddLessonButton';
import { Section, useMoveLessonMutation, useMoveModuleMutation } from '../../../../api/graphql-codegen';
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
  DraggableProvided,
  DroppableProvided,
  DraggableStateSnapshot
} from 'react-beautiful-dnd';
import { useEditUnit } from '../edit/useEditUnit';

type Props = {
  title: string;
  unitId: string;
  lessons: LessonType[];
  refetchUnit: () => void;
  refetchSchoolUnit: () => void;
};

const reorder = (list: any, startIndex: number, endIndex: number) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result;
};

function Presentations(props: Props) {
  const { title, unitId, lessons, refetchUnit, refetchSchoolUnit } = props;
  const [ lessonOpen, setLessonOpen ] = useState<string | null>(null);
  const [opened, setOpened] = useState(true);
  const [lessonList, setLessonList] = useState<Array<LessonType>>(lessons ?? [])
  const [moveLesson] = useMoveLessonMutation();
  const [moveModule] = useMoveModuleMutation();
  const { unit, setUnit } = useEditUnit();

  useEffect(() => {
    const lessonList = [...lessons ?? []].sort((a,b) => (a?.number ?? 10000) - (b?.number ?? 0))
    setLessonList(lessonList as LessonType[]);
  }, [lessons])

  const handleToggle = (e: MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    const _opened = !opened;
    setOpened(_opened);
  };

  const handleCreatedLesson = () => {
    refetchUnit();
  };

  const wrapperClassName = useMemo(() => {
    const classes = [styles.wrapper];
    if (opened) {
      classes.push(styles.opened);
    }

    return classes.join(' ');
  }, [opened]);

  const handleDragEnd = useCallback((result: DropResult) => {
    if (!result.destination) {
      return;
    }

    const sourceIndex = result.source.index;
    const destIndex = result.destination.index;
    if (result.type === "droppableItem") {

      let items = reorder(lessonList, sourceIndex, destIndex) as LessonType[];
      items = items.map((item, index) => {
        return {...item, number: index + 1}
      })
      setLessonList(items);

      const selectedLesson = lessonList.find((lesson, index) => index === result.source!.index && lesson.id);
      const newpos = result.destination!.index + 1
      const oldpos = result.source!.index + 1

      moveLesson({
        variables: {
          input: {
            unitId,
            lessonId: selectedLesson!.id,
            oldPosition: oldpos,
            newPosition: newpos
          }
        }
      })
    } else if (result.type === "droppableSectionModuleItem") {
      const sourceId = result.source.droppableId.split('_____');
      const destId = result.destination.droppableId.split('_____');
      const sourceParentId = sourceId[0];
      const destParentId = destId[0];
      const sourceSectionId = sourceId[1];
      const destSectionId = destId[1];
      if (sourceParentId === destParentId) {
        const selectedModule = (lessonList.filter(l => l.id === sourceParentId)[0].sections?.filter(s => s.id === sourceSectionId)[0].modules as Content[]).find((content: Content, index) => index === result.source!.index && content.id);
        moveModule({
          variables: {
            input: {
              moduleId: selectedModule!.id,
              oldPosition: {
                unitId: unitId,
                lessonId: sourceParentId,
                sectionId: sourceSectionId,
                position: result.source!.index + 1
              },
              newPosition: {
                unitId: unitId,
                lessonId: destParentId,
                sectionId: destSectionId,
                position: result.destination!.index + 1
              }
            }
          }
        });
        let newItems = [...lessonList ?? []];
        newItems = newItems.map((item) => {
          if (item?.id === sourceParentId) {
            if (sourceSectionId === destSectionId) {
              const newSections = item.sections?.map(section => {
                if (section.id === sourceSectionId) {
                  return { ...section, modules: reorder(section.modules, sourceIndex, destIndex) } as Section;
                }
                return section;
              });
              return { ...item, sections: newSections }
            } else {
              const sourceSection = item.sections?.find(s => s.id === sourceSectionId);
              const destSection = item.sections?.find(s => s.id === destSectionId);

              let newSourceSubItems = [...sourceSection!.modules!];
              const [draggedItem] = newSourceSubItems.splice(sourceIndex, 1);
              let newDestSubItems = [...destSection!.modules!];
              newDestSubItems.splice(destIndex, 0, draggedItem);

              const newSections = item.sections?.map(section => {
                if (section.id === sourceSectionId) {
                  return {
                    ...section,
                    modules: newSourceSubItems
                  }
                }
                if (section.id === destSectionId) {
                  return {
                    ...section,
                    modules: newDestSubItems
                  }
                }
                return section;
              });
              return { ...item, sections: newSections }
            }
          }
          return item;
        });
        setLessonList(newItems as LessonType[]);
        if (unit) {
          const classPresentation = [...(newItems || [])]
          setUnit({...unit, classPresentation});
        }
      }
    } else if (result.type === "droppableModuleItem") {
      const sourceParentId = result.source.droppableId;
      const destParentId = result.destination.droppableId;
      const itemModuleItemMap = lessonList.reduce((acc: any, item) => {
        acc[item.id] = item.modules;
        return acc;
      }, {});

      const sourceSubItems = itemModuleItemMap[sourceParentId];
      const destSubItems = itemModuleItemMap[destParentId];

      var newItems = [...lessonList ?? []];

      if (sourceParentId === destParentId) {
        /** In this case subItems are reOrdered inside same Parent */
        const reorderedSubItems = reorder(
          sourceSubItems,
          sourceIndex,
          destIndex
        );

        newItems = newItems.map((item) => {
          if (item?.id === sourceParentId) {
            return {...item, modules: reorderedSubItems as any[]};
          }
          return item;
        });
        setLessonList(newItems as LessonType[]);
        if (unit) {
          const classPresentation = [...(newItems || [])]
          setUnit({...unit, classPresentation});
        }
        const selectedModule = (reorderedSubItems as Content[]).find((content: Content, index) => index === result.destination!.index && content.id);
        moveModule({
          variables: {
            input: {
              moduleId: selectedModule!.id,
              oldPosition: {
                lessonId: sourceParentId,
                unitId: unitId,
                position: result.source!.index + 1
              },
              newPosition: {
                lessonId: destParentId,
                unitId: unitId,
                position: result.destination!.index + 1
              }
            }
          }
        });
      } else {
        let newSourceSubItems = [...sourceSubItems];
        const [draggedItem] = newSourceSubItems.splice(sourceIndex, 1);

        let newDestSubItems = [...destSubItems];
        newDestSubItems.splice(destIndex, 0, draggedItem);

        newItems = newItems.map((item) => {
          if (item?.id === sourceParentId) {
            return {...item, modules: newSourceSubItems};
          } else if (item?.id === destParentId) {
            return {...item, modules: newDestSubItems};
          }
          return item;
        });
        if (unit) {
          const classPresentation = [...(newItems || [])]
          setUnit({...unit, classPresentation});
        }
        setLessonList(newItems as LessonType[]);
        const selectedModule = (newDestSubItems as Content[]).find((content: Content, index) => index === result.destination!.index && content.id);
        moveModule({
          variables: {
            input: {
              moduleId: selectedModule!.id,
              oldPosition: {
                lessonId: sourceParentId,
                unitId: unitId,
                position: result.source!.index + 1
              },
              newPosition: {
                lessonId: destParentId,
                unitId: unitId,
                position: result.destination!.index + 1
              }
            }
          }
        });
      }
    } else if (result.type === "droppableMaterialItem") {
      const sourceParentId = result.source.droppableId.replace('material-', '');
      const destParentId = result.destination.droppableId.replace('material-', '');
      const itemMaterialItemMap = lessonList.reduce((acc: any, item) => {
        acc[item.id] = item.materials;
        return acc;
      }, {});

      const sourceSubItems = itemMaterialItemMap[sourceParentId];
      const destSubItems = itemMaterialItemMap[destParentId];

      var newLessons = [...lessonList ?? []];

      if (sourceParentId === destParentId) {
        /** In this case subItems are reOrdered inside same Parent */
        const reorderedSubItems = reorder(
          sourceSubItems,
          sourceIndex,
          destIndex
        );

        newLessons = newLessons.map((item) => {
          if (item?.id === sourceParentId) {
            return {...item, materials: reorderedSubItems as any[]};
          }
          return item;
        });
        setLessonList(newLessons as LessonType[]);
        if (unit) {
          const classPresentation = [...(newLessons || [])]
          setUnit({...unit, classPresentation});
        }
        const selectedModule = (reorderedSubItems as Content[]).find((content: Content, index) => index === result.destination!.index && content.id);
        moveModule({
          variables: {
            input: {
              moduleId: selectedModule!.id,
              lessonMaterial: true,
              oldPosition: {
                lessonId: sourceParentId,
                unitId: unitId,
                position: result.source!.index + 1
              },
              newPosition: {
                lessonId: destParentId,
                unitId: unitId,
                position: result.destination!.index + 1
              }
            }
          }
        });
      } else {
        let newSourceSubItems = [...sourceSubItems];
        const [draggedItem] = newSourceSubItems.splice(sourceIndex, 1);

        let newDestSubItems = [...destSubItems];
        newDestSubItems.splice(destIndex, 0, draggedItem);

        newLessons = newLessons.map((item) => {
          if (item?.id === sourceParentId) {
            return {...item, materials: newSourceSubItems};
          } else if (item?.id === destParentId) {
            return {...item, materials: newDestSubItems};
          }
          return item;
        });
        if (unit) {
          const classPresentation = [...(newLessons || [])]
          setUnit({...unit, classPresentation});
        }
        setLessonList(newLessons as LessonType[]);
        const selectedModule = (newDestSubItems as Content[]).find((content: Content, index) => index === result.destination!.index && content.id);
        moveModule({
          variables: {
            input: {
              moduleId: selectedModule!.id,
              lessonMaterial: true,
              oldPosition: {
                lessonId: sourceParentId,
                unitId: unitId,
                position: result.source!.index + 1
              },
              newPosition: {
                lessonId: destParentId,
                unitId: unitId,
                position: result.destination!.index + 1
              }
            }
          }
        });
      }
    }
  }, [unitId, lessonList, unit, setUnit, moveLesson, moveModule]);

  if (!lessonList) return null;

  return (
    <div className={wrapperClassName}>
      <div onClick={handleToggle} className={styles.header}>
        <RightIcon className={styles.rightIcon}/>
        <div className={styles.title}>{title}</div>
      </div>
      { opened && <>
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="droppable" type="droppableItem">
            {(droppableProvided: DroppableProvided) => (
              <div
                ref={droppableProvided.innerRef}
                {...droppableProvided.droppableProps}
              >
                {lessonList.map((item: LessonType, index: number) => (
                  <Draggable
                    key={item.id}
                    draggableId={item.id}
                    index={index}
                  >
                    {(
                      draggableProvided: DraggableProvided,
                      snapshot: DraggableStateSnapshot
                    ) => (
                        <div
                          ref={draggableProvided.innerRef}
                          {...draggableProvided.draggableProps}
                          className={snapshot.isDragging ? styles.dragableItem : ''}
                        >
                          <Lesson draggableProvided={draggableProvided} refetchUnit={refetchSchoolUnit} snapshot={snapshot} key={item.id} lesson={item} setLessonOpen={setLessonOpen} lessonOpen={lessonOpen}/>
                        </div>
                      )
                    }
                  </Draggable>
                ))}
                {droppableProvided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      <div className={styles.addContentWrapper}>
        <AddLessonButton unitId={unitId} onCreated={handleCreatedLesson}/>
      </div>
    </>
    }
    </div>
  );
}

export default Presentations;
